export const CONTENT = {
  hero: {
    tagline: 'The development environment for robotics.',
    subcopy:
      'Describe your robot, wire its behavior from modules. Compile it to a native binary for your hardware. Run ROS as a module for deeper debugging metrics.',
    wordmark: 'SHINR',
    primaryCta: 'Talk to us',
    secondaryCta: 'Login',
  },
  problem: {
    eyebrow: 'THE PROBLEM',
    headingLead: 'Teams rebuild the same plumbing instead of shipping what makes their robot ',
    headingHighlight: 'unique',
    bodyLead:
      'Every robotics team writes its own drivers, deployment pipelines, debug tooling, and hardware abstraction. Vendor SDKs lock you to one platform. Internal stacks take years to develop. ',
    bodyHighlight: 'Physical AI requires fast iteration',
    statLead: 'Too much robotics engineering time disappears into ',
    statTail: 'non-differentiating infrastructure.',
    diagram: {
      columnHeaderLeft: 'WHAT MAKES EACH ROBOT UNIQUE',
      columnHeaderRight: 'THE PLUMBING THEY EACH REBUILD',
      lanes: [
        { label: '01', title: 'AUTONOMOUS VEHICLES', caption: 'fleet-scale autonomy' },
        { label: '02', title: 'ROBOTIC ARMS', caption: 'sub-millimetre manipulation' },
        { label: '03', title: 'HUMANOID ROBOTS', caption: 'whole-body control' },
      ],
      deckLabel: 'IN-HOUSE STACK',
      deckItems: ['HAL', 'drivers', 'vendor SDKs', 'launch files', 'sim bridges', 'deploy hacks', 'logging glue'],
      deckBadge: '×3',
      thesisLead: 'Different robots. ',
      thesisHighlight: 'The same plumbing',
      thesisTail: ', rebuilt from scratch, every time.',
    },
  },
  whyNow: {
    eyebrow: 'WHY NOW',
    heading: 'Two shifts created the gap.',
    card1Label: 'HARDWARE IS HETEROGENEOUS NOW',
    card1Body:
      'Five years ago a team picked one platform. Today every team spans compute targets: Jetson for perception, ESP32 for low-level control, custom boards for accelerators. Existing tooling assumes one target.',
    card2Label: 'AI IS THE APPLICATION LAYER',
    card2Body:
      'Robot behavior is composed from learned policies, perception models, and controllers. Every team builds the integration plumbing itself. The development environment for AI-native robotics does not exist yet.',
    tagline:
      'every tech wave produced the company that owned the workbench. games got Unreal. code got GitHub. data got Databricks. robotics still rebuilds the workbench project by project.',
  },
  moat: {
    eyebrow: 'WHY SHINRO',
    heading: 'Same stack. New foundation.',
    headingLines: ['Same stack', 'New foundation'],
    subHeadline: 'Build once, run on anything.',
    bodyLines: [
      'Shinro sits between robotics frameworks and hardware',
      'targets: one consistent way to compose, build, deploy,',
      'and observe robot software across runtimes.',
    ],
    bodyBoldLine: 'Run your stack with less runtime plumbing.',
    toggleBefore: 'your stack',
    toggleAfter: 'with shinro',
    rows: [
      { code: 'L4', label: 'APPLICATION', chips: ['fleet', 'ui'] },
      { code: 'L3', label: 'PLANNING', chips: ['nav', 'behaviors'] },
      { code: 'L2', label: 'STATE ESTIMATION', chips: ['slam', 'perception'] },
      { code: 'L1', label: 'DRIVERS', chips: ['control', 'dds'] },
    ],
    hardware: { code: 'L0', label: 'HARDWARE', chips: ['compute', 'sensors', 'motors'] },
    kernelLabel: 'SHINRO KERNEL',
    swapSuffix: '· no restart',
    sealedCaption: 'A traditional layered stack. Each layer talks to its neighbors, and a change means rebuild and restart.',
    liveCaption: 'One thin layer above hardware. Connecting any module to every layer.',
    liveCaptionLines: [
      'One thin layer above hardware',
      'Connecting any module to every layer',
    ],
  },
  product: {
    eyebrow: 'Studio',
    heading:
      'One environment: describe the machine, compose behavior, simulate, deploy native, swap live.',
    subHeadline: 'See everything.',
    card1Label: 'Visual Programming',
    card1Caption: 'Drag, drop, and link blocks to map hardware and orchestrate behavior.',
    card2Label: 'HIGH-FIDELITY FEEDBACK',
    card2Caption: 'Real-time physics for pre-hardware validation, live telemetry after deployment.',
    oscLabel: 'JOINT TORQUE / Nm',
    oscValue: '2.14',
    card3Label: 'CODE-TO-HARDWARE PIPELINES',
    card3Caption: 'Pick your target device and settings, then swap live modules at low latency.',
    targets: ['Jetson Orin', 'ESP32-S3', 'x86 sim'],
    targetSelected: 'selected',
    targetHeader: 'target architecture',
  },
  runtime: {
    eyebrow: 'THE ARCHITECTURE',
    heading: 'Where composition happens.',
    leftLabel: 'MOST FRAMEWORKS',
    leftTitle: 'Composed at runtime',
    leftBody: 'Components find each other and exchange messages while the robot runs.',
    leftNodes: ['camera driver', 'SLAM', 'planner', 'motor control'],
    busLabel: 'message bus · shared memory',
    leftCaption: 'Wiring is resolved while the robot runs. Every hop crosses the bus.',
    rightLabel: 'SHINRO',
    rightTitle: 'Composed at compile time',
    rightBody: 'The toolchain reads your module descriptors and generates the inter-process plumbing into one binary.',
    descriptorsLabel: 'MODULE DESCRIPTORS',
    buildCmd: '$ cake build',
    buildNote: 'generates the plumbing',
    binaryTitle: 'one native binary',
    binaryNote: 'cross-compiled for the target',
    plumbingCaption: 'generated plumbing: shared memory, pipes, concurrency',
    kernelTitle: 'cake kernel',
    kernelNote: 'distributed as-is',
    hardwareCaption: 'target hardware: any architecture',
    rightCaption: 'Deploying updates a module on the kernel, at runtime. The rest keeps running.',
    resultLead: 'THE RESULT',
    resultChips: ['less runtime discovery', 'fewer moving parts', 'more auditable builds', 'cleaner deployment boundaries', 'live module updates'],
    takeaway: 'Shinro moves the hard part from runtime to compile time.',
  },
  loop: {
    eyebrow: 'THE WORKFLOW',
    heading: 'The developer loop.',
    steps: [
      { code: '01', title: 'Develop', body: 'Compose in Shinro Studio, or reuse existing modules.' },
      { code: '02', title: 'Build', body: 'The kernel toolchain cross-compiles your modules for the target architectures.', cmd: '$ cake build' },
      { code: '03', title: 'Deploy', body: 'Ship to the device, or hot-swap a module at runtime without stopping the robot.' },
      { code: '04', title: 'Observe', body: 'Telemetry streams back from the robot.' },
    ],
    loopBadge: 'back to Studio: simulate, iterate',
    takeaway: 'Develop, build, deploy, observe. Then iterate.',
  },
  components: {
    eyebrow: 'THE PLATFORM',
    heading: 'Three components.',
    rows: [
      { label: 'DEVELOP', title: 'Shinro Studio', body: 'The AI-native visual programming and development platform.' },
      { label: 'SHARE & REUSE', title: 'Reusable modules', body: 'Community-published modules, ready to drop into your build.', chips: ['ROS wrapper', 'SLAM', 'telemetry', 'simulation'] },
      { label: 'RUN', title: 'cake', sub: 'the Shinro kernel', body: 'A low-latency, cross-platform executable. Distributed as-is, deployable to any architecture.', meta: 'arm64 · x86-64 · MCU-class', live: true },
    ],
    takeaway: 'Develop in Studio · run on cake · share what you build.',
  },
  licensing: {
    eyebrow: 'LICENSING',
    heading: 'What is open. What is licensed.',
    rows: [
      { title: 'Shinro Studio', body: 'Source in the open, free to use. Fair-code terms.', badge: 'FAIR-CODE', emphasis: true },
      { title: 'Modules', body: "Each module carries its author's license. Open source encouraged.", badge: 'PER-MODULE', emphasis: true },
      { title: 'cake', sub: 'the kernel', body: 'Commercially copyrighted, distributed as-is.', badge: 'CLOSED SOURCE' },
    ],
    takeaway: 'Open tools to build with, a dependable kernel to run on.',
  },
  // 2026-07-24 design update (design-export/2026-07-24-livemodules). ArmTwin's status
  // strings stay inline in the molecule (spec §7). Heading re-authored 2026-07-25 by direct
  // user request (lead + green highlight, ProblemRegion split pattern) — original copy,
  // no design-export equivalent; the region renders lead + highlight + a plain period.
  liveModules: {
    eyebrow: 'LIVE MODULES',
    headingLead: 'Live module updates ',
    headingHighlight: 'without downtime',
    subhead: 'Add hardware capabilities or replace software behaviour without stopping the task.',
  },
  comeBuild: {
    heading: 'Come build robot software that stays live.',
  },
  cta: {
    tryShinro: 'Try Shinro',
    tryShinroHref: 'https://shinro-studio-web.vercel.app/',
    github: 'GitHub',
    githubHref: 'https://github.com/Shinro-xyz',
    community: 'Community',
    communityHref: 'https://github.com/orgs/shinro-dev',
    docs: 'Docs',
    docsHref: 'https://docs.shinro.dev',
  },
  nav: {
    product: 'Workflows',
    architecture: 'Architecture',
    modules: 'Modules',
    licensing: 'Licensing',
    github: 'GitHub',
    githubHref: 'https://github.com/Shinro-xyz',
    docs: 'Docs',
    docsHref: 'https://docs.shinro.dev',
    login: 'Login',
  },
  footer: {
    brand: 'SHINRO',
    tagline: 'The development environment for robotics.',
    // Boilerplate clause (mission Phase 2 §7, fair-code wording approved 2026-07-25)
    fairCode: 'Source-available under fair-code terms.',
    cake: 'CAKE / FIRST RELEASE SHIPPED',
    studio: 'STUDIO / IN DEVELOPMENT',
    copyright: '(C) 2026 SHINRO',
  },
  hint: {
    drag: 'drag to explore',
    zoom: 'scroll to zoom',
    tourPrefix: 'press ',
    tourKey: 'space',
    tourSuffix: ' to run the tour',
  },
  hud: {
    runTour: 'Run tour',
    map: 'MAP',
    resetTitle: 'Reset to 100%',
    fitTitle: 'Fit to view (F)',
  },
} as const
